/**
 * Starts assistant turns and yields the contract's events.
 *
 * Real mode: POST /assistant/threads/:threadId/turns on the brand API and read
 * the SSE body (streaming contract, "Transport"). Stub mode
 * (VITE_ASSISTANT_STUB=true): replay the shared fixtures with no network, so
 * the panel runs before C2 exists.
 */
import { ASSISTANT_STUB } from "@/config/features";
import { parseSseStream } from "@/lib/sse";
import { currentPortalHost } from "@/lib/portalHost";
import { brandApi } from "@/lib/axios";
import { isAxiosError } from "axios";
import type {
  AssistantAllowance,
  AssistantErrorCode,
  AssistantEvent,
  ProposalCancelResult,
  ProposalConfirmResult,
  StartTurnRequest,
} from "@/types/assistant";
import {
  allowanceExceeded,
  askTurn,
  doTurn,
  pickFixture,
  proposalConfirmed,
  replay,
} from "@/components/assistant/fixtures/streams";

/** A failure before the stream opened (JSON + HTTP status) or an `error` event. */
export class AssistantRequestError extends Error {
  constructor(
    public readonly code: AssistantErrorCode,
    message: string,
    public readonly retryable = false,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "AssistantRequestError";
  }
}

export interface AssistantMe {
  enabled: boolean;
  /** null when disabled */
  allowance: AssistantAllowance | null;
}

/** Whether the signed-in brand has the assistant, and its allowance before the first turn. */
export async function getAssistantMe(): Promise<AssistantMe> {
  if (ASSISTANT_STUB) {
    return { enabled: true, allowance: { used: 1, limit: 200, resetsAt: "2026-11-01T00:00:00Z" } };
  }
  const { data } = await brandApi.get<AssistantMe>("/assistant/me");
  return data;
}

const EVENT_NAMES = new Set<AssistantEvent["name"]>([
  "turn.start",
  "text.delta",
  "tool.start",
  "tool.end",
  "proposal",
  "turn.end",
  "error",
]);

function brandHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Portal-Host": currentPortalHost(),
  };
  const token = localStorage.getItem("brand_token");
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function toRequestError(res: Response): Promise<AssistantRequestError> {
  let body: { code?: AssistantErrorCode; message?: string; retryable?: boolean } = {};
  try {
    body = await res.json();
  } catch {
    /* non-JSON body: fall through to the status mapping */
  }
  const code: AssistantErrorCode =
    body.code ??
    (res.status === 404
      ? "FEATURE_DISABLED"
      : res.status === 409
        ? "TURN_IN_PROGRESS"
        : res.status === 429
          ? "RATE_LIMITED"
          : res.status === 503
            ? "UPSTREAM_UNAVAILABLE"
            : "INTERNAL");
  return new AssistantRequestError(code, body.message ?? res.statusText, body.retryable ?? false, res.status);
}

/** Start a turn. Yields events until `turn.end`; throws AssistantRequestError on failure. */
export async function* startTurn(
  req: StartTurnRequest,
  signal?: AbortSignal,
): AsyncGenerator<AssistantEvent> {
  if (ASSISTANT_STUB) {
    const which = pickFixture(req.message);
    if (which === "allowance") {
      const { status, ...e } = allowanceExceeded;
      throw new AssistantRequestError(e.code, e.message, e.retryable, status);
    }
    yield* replay(which === "do" ? doTurn : askTurn, { signal });
    return;
  }

  const base = import.meta.env.VITE_API_URL as string;
  const res = await fetch(`${base}/assistant/threads/${encodeURIComponent(req.threadId)}/turns`, {
    method: "POST",
    headers: brandHeaders(),
    body: JSON.stringify({
      message: req.message,
      clientTurnId: req.clientTurnId,
      ...(req.context ? { context: req.context } : {}),
    }),
    signal,
  });

  if (!res.ok || !res.body) throw await toRequestError(res);

  for await (const frame of parseSseStream(res.body, signal)) {
    if (!EVENT_NAMES.has(frame.event as AssistantEvent["name"])) continue; // forward-compatible
    let payload: Omit<AssistantEvent, "name">;
    try {
      payload = JSON.parse(frame.data);
    } catch {
      continue;
    }
    const event = { name: frame.event, ...payload } as AssistantEvent;
    if (event.name === "error") {
      throw new AssistantRequestError(event.code, event.message, event.retryable);
    }
    yield event;
  }
}

/** Ask the backend to stop a running turn; the stream then ends with `cancelled`. */
export async function cancelTurn(turnId: string): Promise<void> {
  if (ASSISTANT_STUB) return;
  const base = import.meta.env.VITE_API_URL as string;
  await fetch(`${base}/assistant/turns/${encodeURIComponent(turnId)}/cancel`, {
    method: "POST",
    headers: brandHeaders(),
  }).catch(() => undefined);
}

/* ------------------------------------------------------------------ */
/* Proposals (U2). The write runs on the server from its stored copy;   */
/* the panel only says yes or no.                                        */
/* ------------------------------------------------------------------ */

/** An axios failure from a proposal endpoint → the contract's error shape. */
function toProposalError(err: unknown): AssistantRequestError {
  if (isAxiosError(err) && err.response) {
    const body = (err.response.data ?? {}) as {
      code?: AssistantErrorCode;
      message?: string;
      retryable?: boolean;
    };
    return new AssistantRequestError(
      body.code ?? (err.response.status === 404 ? "PROPOSAL_NOT_FOUND" : "INTERNAL"),
      body.message ?? "Reveshare couldn't make this change. Nothing was saved.",
      body.retryable ?? false,
      err.response.status,
    );
  }
  return new AssistantRequestError("UPSTREAM_UNAVAILABLE", "Reveshare couldn't be reached. Try again.", true);
}

const stubDelay = () => new Promise<void>((r) => setTimeout(r, 900));

/** Run the proposed change. Resolves with the backend's outcome line, or throws AssistantRequestError. */
export async function confirmProposal(proposalId: string): Promise<ProposalConfirmResult> {
  if (ASSISTANT_STUB) {
    await stubDelay();
    return { ...proposalConfirmed, proposalId };
  }
  try {
    const { data } = await brandApi.post<ProposalConfirmResult>(
      `/assistant/proposals/${encodeURIComponent(proposalId)}/confirm`,
    );
    return data;
  } catch (err) {
    throw toProposalError(err);
  }
}

/** Decline the proposed change. Nothing is written either way. */
export async function cancelProposal(proposalId: string): Promise<ProposalCancelResult> {
  if (ASSISTANT_STUB) {
    await stubDelay();
    return { proposalId, status: "CANCELLED" };
  }
  try {
    const { data } = await brandApi.post<ProposalCancelResult>(
      `/assistant/proposals/${encodeURIComponent(proposalId)}/cancel`,
    );
    return data;
  } catch (err) {
    throw toProposalError(err);
  }
}
